<template>
  <GoLiveConfirmation />
  <!-- The mentor's incoming request: centred over a dimmed page, so it cannot
       be missed. It closes only through Join, Not now or the request ending.
       It shakes in step with the ring, every 3 seconds. -->
  <Transition
    enter-active-class="transition duration-200 ease-out"
    enter-from-class="opacity-0"
    leave-active-class="transition duration-150 ease-in"
    leave-to-class="opacity-0"
  >
    <div
      v-if="store.enabled && store.incoming"
      class="fixed inset-0 z-[100] flex items-center justify-center bg-neutral-900/60 p-4 backdrop-blur-sm"
    >
      <div class="incoming-pop w-full max-w-md">
        <IncomingTalkRequest :request="store.incoming" class="incoming-ring" />
      </div>
    </div>
  </Transition>

  <!-- The member's side: waiting, or not answered. Small, bottom right. -->
  <div
    v-if="
      store.enabled && !store.incoming && (store.outgoing || store.unanswered)
    "
    class="gm-above-tabbar fixed inset-x-0 bottom-0 z-50 flex justify-center p-4 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:p-0"
  >
    <WaitingForMentor />
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, watch } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { useActionCable } from '@/shared/composables/useActionCable'
import { INSTANT_SESSIONS_CHANNEL } from '@/shared/data/actionCableChannels.js'
import { useInstantSessionsStore } from '@/modules/instant-sessions/stores/instantSessions'
import type {
  InstantChannelMessage,
  InstantSessionsShared,
} from '@/modules/instant-sessions/types'
import { unlockAudio } from '@/modules/instant-sessions/utils/alerts'
import IncomingTalkRequest from './IncomingTalkRequest.vue'
import WaitingForMentor from './WaitingForMentor.vue'
import GoLiveConfirmation from './GoLiveConfirmation.vue'

// Mounted once in the member layout. It keeps the channel open on every page,
// so a live mentor gets a request wherever they are, and sends the heartbeat
// that keeps them live.
const HEARTBEAT_MS = 30_000

const page = usePage()
const store = useInstantSessionsStore()

const { subscribeChannel, unsubscribeChannel, performAction } = useActionCable({
  channelName: INSTANT_SESSIONS_CHANNEL,
  onReceived: (data) => store.handleMessage(data as InstantChannelMessage),
})

let heartbeat: number | null = null
const startHeartbeat = () => {
  if (heartbeat !== null) return
  heartbeat = window.setInterval(() => {
    if (store.isLive) performAction({ action: 'heartbeat', data: {} })
  }, HEARTBEAT_MS)
}
const stopHeartbeat = () => {
  if (heartbeat !== null) window.clearInterval(heartbeat)
  heartbeat = null
}

watch(
  () =>
    (page.props.shared as { instantSessions?: InstantSessionsShared | null })
      .instantSessions,
  (shared, previous) => {
    store.hydrate(shared)
    if (shared && !previous) {
      subscribeChannel()
      startHeartbeat()
    } else if (!shared && previous) {
      unsubscribeChannel()
      stopHeartbeat()
    }
  },
  { immediate: true },
)

// A full page load starts audio suspended. The mentor's first click on the
// page resumes it, so a later request can ring.
const unlockOnFirstClick = () => {
  if (store.canGoLive) unlockAudio()
}
onMounted(() => {
  window.addEventListener('pointerdown', unlockOnFirstClick, { once: true })
})

onBeforeUnmount(() => {
  stopHeartbeat()
  window.removeEventListener('pointerdown', unlockOnFirstClick)
})
</script>

<style scoped>
.incoming-pop {
  animation: incoming-pop 320ms cubic-bezier(0.2, 0.9, 0.3, 1.2) both;
}
@keyframes incoming-pop {
  from {
    opacity: 0;
    transform: scale(0.92) translateY(8px);
  }
}
@media (prefers-reduced-motion: reduce) {
  .incoming-pop {
    animation: none;
  }
}
/* A short shake at the start of each 3 s ring, then still. */
@media (prefers-reduced-motion: no-preference) {
  .incoming-ring {
    animation: incoming-ring 3s ease-in-out infinite;
  }
}
@keyframes incoming-ring {
  0%,
  20%,
  100% {
    transform: none;
  }
  2%,
  6%,
  10%,
  14% {
    transform: translateX(-5px) rotate(-1.5deg);
  }
  4%,
  8%,
  12%,
  16% {
    transform: translateX(5px) rotate(1.5deg);
  }
  18% {
    transform: translateX(-2px) rotate(-0.5deg);
  }
}
</style>
